// ABOUTME: Dual-axis chart showing positive vs negative behavior ratios and intervention counts
// ABOUTME: Combines line chart for ratio trends with bar charts for squares and Let's Reset counts

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceArea,
  PieChart,
  Pie,
  Cell,
  TooltipContentProps
} from 'recharts';
import { alpha } from '@mui/material/styles';
import { Child, BehaviorType, BehaviorRecord } from '../types/api';
import { behaviorAPI } from '../services/api';
import { starAccent } from '../theme/tokens';
import { getMobileChartMargins, MOBILE_BREAKPOINT_QUERY } from '../utils/mobileOptimization';
import { getTodayInFamilyTimezone, getDateInFamilyTimezone } from '../utils/dateUtils';
import naluV1Theme from '../theme/theme.nalu-v1';

interface PositiveNegativeBehaviorsChartProps {
  child: Child;
  familyId: number;
  familyTimezone: string;
}

interface ChartDataPoint {
  date: string;
  ratio: number; // 0-100
  squareCount: number;
  flipYourCoinCount: number;
  starCount: number; // for tooltip
  triangleCount: number; // for tooltip
}

const PositiveNegativeBehaviorsChart: React.FC<PositiveNegativeBehaviorsChartProps> = ({
  child,
  familyId,
  familyTimezone
}) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const chartMargins = getMobileChartMargins('composedChart', isMobile);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [behaviorData, setBehaviorData] = useState<BehaviorRecord[]>([]);

  // Calculate 30-day date range using family timezone utilities
  const dateRange = useMemo(() => {
    // Use the existing timezone-aware utility instead of buggy new Date() conversion
    const today = getTodayInFamilyTimezone(familyTimezone);
    const todayDate = new Date(today + 'T00:00:00.000Z');
    const startDate = new Date(todayDate);
    startDate.setDate(startDate.getDate() - 29); // 30 days including today

    return {
      startDate: getDateInFamilyTimezone(startDate, familyTimezone),
      endDate: today
    };
  }, [familyTimezone]);

  // Load behavior data
  useEffect(() => {
    const loadBehaviorData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const childId = child.id || child.childId;
        if (!childId) {
          throw new Error('Child ID is required');
        }

        const response = await behaviorAPI.getBehaviorRecords(
          familyId,
          dateRange.startDate,
          dateRange.endDate,
          childId,
          undefined, // behaviorTypes
          undefined, // days
          1, // page
          25, // pageSize
          false // paginate - get all results for date range (Issue #1089)
        );

        setBehaviorData(response.data || []);
      } catch (err) {
        console.error('Failed to load behavior data:', err);
        setError('Error loading behavior data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadBehaviorData();
  }, [child.id, child.childId, familyId, dateRange.startDate, dateRange.endDate]);

  // Process data for chart
  const chartData = useMemo((): ChartDataPoint[] => {
    // Filter behaviors for this specific child
    const childId = child.id || child.childId;
    const childBehaviors = behaviorData.filter(behavior => behavior.childId === childId);

    if (childBehaviors.length === 0) {
      return [];
    }

    // Create map of date -> behaviors using family timezone
    const behaviorsByDate = new Map<string, BehaviorRecord[]>();
    childBehaviors.forEach(behavior => {
      // Use eventLocalTime for timeline-stable behavior placement (Issue #312, #1089)
      // eventLocalTime represents when the behavior actually occurred in family timezone
      const eventTimeString = behavior.eventLocalTime || behavior.recordedAt;
      const behaviorDate = new Date(eventTimeString);
      const dateInFamilyTz = getDateInFamilyTimezone(behaviorDate, familyTimezone);
      const date = dateInFamilyTz.split('T')[0]; // Get YYYY-MM-DD portion
      if (!behaviorsByDate.has(date)) {
        behaviorsByDate.set(date, []);
      }
      behaviorsByDate.get(date)!.push(behavior);
    });

    // Generate all dates in range using simple string iteration to avoid timezone conversion
    const result: ChartDataPoint[] = [];
    const startParts = dateRange.startDate.split('-').map(Number);
    const endParts = dateRange.endDate.split('-').map(Number);

    const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]); // Local date
    const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]); // Local date

    const current = new Date(startDate);
    while (current <= endDate) {
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const dayBehaviors = behaviorsByDate.get(dateStr) || [];

      // Count behaviors by type
      const starCount = dayBehaviors.filter(b => b.behaviorType === BehaviorType.Star).length;
      const triangleCount = dayBehaviors.filter(b => b.behaviorType === BehaviorType.Triangle).length;
      const squareCount = dayBehaviors.filter(b => b.behaviorType === BehaviorType.Square).length;
      const flipYourCoinCount = dayBehaviors.filter(b => b.behaviorType === BehaviorType.FlipYourCoin).length;

      // Calculate ratio
      const total = starCount + triangleCount;
      const ratio = total > 0 ? (starCount / total) * 100 : 0;

      result.push({
        date: dateStr,
        ratio: Math.round(ratio * 10) / 10, // Round to 1 decimal
        squareCount,
        flipYourCoinCount,
        starCount,
        triangleCount
      });

      current.setDate(current.getDate() + 1);
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- child.childId, child.id, and familyTimezone intentionally omitted; behaviorData already encapsulates child-specific filtered data
  }, [behaviorData, dateRange.startDate, dateRange.endDate]);

  // Format date for display
  const formatDate = (dateStr: string) => {
    const parts = dateStr.split('-').map(Number);
    const date = new Date(parts[0], parts[1] - 1, parts[2]); // Local date construction
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper function to render small donut chart for tooltip
  const renderTooltipDonut = (stars: number, triangles: number) => {
    const total = stars + triangles;

    // Calculate ratio in X:1 format like the card above
    const ratio = total === 0
      ? '0:0'
      : triangles === 0
        ? `${stars}:0`
        : `${Math.round(stars / triangles * 10) / 10}:1`;

    if (total === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            height: 40,
          }}
          data-testid="hover-ratio-donut"
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '50%',
              backgroundColor: theme.palette.grey[100],
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
              No data
            </Typography>
          </Box>
          <Typography
            variant="body2"
            fontWeight="bold"
            color="text.secondary"
            sx={{
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              height: 40
            }}
          >
            {ratio}
          </Typography>
        </Box>
      );
    }

    const donutData = [
      { name: 'Stars', value: stars, color: theme.palette.success.main },
      { name: 'Triangles', value: triangles, color: theme.palette.warning.main }
    ].filter(item => item.value > 0);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          height: 40,
        }}
        data-testid="hover-ratio-donut"
        data-star-percentage={Math.round((stars / total) * 100).toString()}
        data-triangle-percentage={Math.round((triangles / total) * 100).toString()}
      >
        <Box sx={{ width: 40, height: 40, flexShrink: 0, overflow: 'hidden' }}>
          <PieChart width={40} height={40}>
            <Pie
              data={donutData}
              cx={20}
              cy={20}
              innerRadius={10}
              outerRadius={16}
              dataKey="value"
              animationDuration={0}
            >
              {donutData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </Box>
        <Typography
          variant="body2"
          fontWeight="bold"
          color="primary"
          sx={{
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            height: 40
          }}
        >
          {ratio}
        </Typography>
      </Box>
    );
  };

  // Custom tooltip with donut chart
  const renderTooltip = ({ active, payload, label }: TooltipContentProps<number, string>) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataPoint;
      console.log('Tooltip data:', data); // Debug log

      return (
        <Box
          data-testid="ratio-hover-tooltip"
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: isNaluV1Theme ? '12px' : 1,
            padding: 2,
            boxShadow: isNaluV1Theme ? 'none' : 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 200,
            minHeight: 140,
          }}
        >
          {/* Date at top */}
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 2 }}>
            {formatDate(String(label))}
          </Typography>

          {/* Small donut chart */}
          <Box sx={{ mb: 2 }}>
            {renderTooltipDonut(data.starCount, data.triangleCount)}
          </Box>

          {/* Text content */}
          <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
            Positive Ratio: {data.ratio}%
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.info.main }}>
            Reset: {data.flipYourCoinCount}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
            Storms: {data.squareCount}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
            Stars: {data.starCount}, Triangles: {data.triangleCount}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Positive to Negative Behaviors
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Positive to Negative Behaviors
          </Typography>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Positive to Negative Behaviors
          </Typography>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No behavior data available for the last 30 days.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Positive to Negative Behaviors
        </Typography>
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={chartMargins}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                fontSize={12}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                fontSize={12}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 10]}
                fontSize={12}
              />
              <Tooltip content={renderTooltip} />
              <Legend />

              {/* Three background zones per Issue #967 - render first so they appear behind other elements */}
              <ReferenceArea
                y1={66}
                y2={100}
                yAxisId="left"
                fill={alpha(theme.palette.success.main, 0.2)}
                data-testid="ratio-green-zone"
              />
              <ReferenceArea
                y1={33}
                y2={66}
                yAxisId="left"
                fill={alpha(starAccent, 0.2)}
                data-testid="ratio-yellow-zone"
              />
              <ReferenceArea
                y1={0}
                y2={33}
                yAxisId="left"
                fill={alpha(theme.palette.error.main, 0.2)}
                data-testid="ratio-red-zone"
              />

              {/* Threshold lines */}
              <ReferenceLine y={33} yAxisId="left" stroke={theme.palette.text.secondary} strokeDasharray="5 5" />
              <ReferenceLine y={66} yAxisId="left" stroke={theme.palette.text.secondary} strokeDasharray="5 5" />

              {/* Bars for interventions */}
              <Bar
                yAxisId="right"
                dataKey="squareCount"
                fill={theme.palette.error.main}
                fillOpacity={0.7}
                name="Storms"
              />
              <Bar
                yAxisId="right"
                dataKey="flipYourCoinCount"
                fill={theme.palette.info.main}
                fillOpacity={0.7}
                name="Reset"
              />

              {/* Line for ratio */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="ratio"
                stroke={theme.palette.primary.main}
                strokeWidth={3}
                dot={{ r: 4 }}
                name="Positive Ratio %"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PositiveNegativeBehaviorsChart;